// 수주품의서 1건의 매출/매입 세금계산서 전체 현황 패널.
//
// 두 군데서 그대로 재사용한다(사용자 요구 2026-10-21):
//   1) 수주품의서 관리(project_orders) 목록의 하위 항목 - 관리부 모니터링 + 재배치/취소
//   2) 재작성 가드(원가산출 해제)가 막을 때 - "무엇이 이미 끝났는지" 체크리스트 + 그 자리에서 취소
//
// 순수 표시용 컴포넌트다. 데이터 로딩/액션 호출은 OrderBillReviewContainer가 한다.
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LockIcon from '@mui/icons-material/Lock';

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};

const STATUS_COLOR = {
  '예정': 'default',
  '발행요청': 'info',
  '결재중': 'warning',
  '발행완료': 'primary',
  '수금(지급)완료': 'success',
};

function StatusChip({ label }) {
  return (
    <Chip
      size="small"
      label={label || '예정'}
      color={STATUS_COLOR[label] || 'default'}
      variant={label === '수금(지급)완료' ? 'filled' : 'outlined'}
      sx={{ fontWeight: 600 }}
    />
  );
}

// 계획(선금/중도금/잔금) 상태 - BillCollectPlanService.decorate()가 내려주는 값 그대로.
const PLAN_STATUS_COLOR = { '예정': 'default', '지연': 'error', '부분수금': 'warning', '완료': 'success' };

function PlanStatusChip({ label, locked }) {
  return (
    <Chip
      size="small"
      icon={locked ? <LockIcon fontSize="small" /> : undefined}
      label={label || '예정'}
      color={PLAN_STATUS_COLOR[label] || 'default'}
      variant={label === '완료' ? 'filled' : 'outlined'}
    />
  );
}

function Totals({ review }) {
  return (
    <Grid container spacing={1} sx={{ mb: 1.5 }}>
      <Grid item xs={4}>
        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">발행액</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{fmt(review.issuedAmt)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={4}>
        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">수금(지급)액</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{fmt(review.collectedAmt)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={4}>
        <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">미수금</Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700 }}
            color={Number(review.outstandingAmt) > 0 ? 'error.main' : 'text.primary'}
          >
            {fmt(review.outstandingAmt)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
}

/** 수금(지급) 내역 표 - 계획별 펼침, 미배정 목록(연결 UI 포함) 둘 다 이걸 재사용한다. */
function CollectRows({ collects, mode, review, row, onCancelCollectRecord, onLinkPlan, plans }) {
  const unfinishedPlans = (plans || []).filter((p) => Number(String(p.remainAmt || '0').replace(/,/g, '')) > 0);
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>일자</TableCell>
          <TableCell align="right">금액</TableCell>
          <TableCell>수단</TableCell>
          <TableCell>처리자</TableCell>
          <TableCell>상태</TableCell>
          {mode === 'manage' && <TableCell align="right">액션</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {collects.map((c) => (
          <TableRow key={c.collectUuid}>
            <TableCell>{c.collectDt || '-'}</TableCell>
            <TableCell align="right">{fmt(c.collectAmt)}</TableCell>
            <TableCell>{c.collectTyp || '-'}</TableCell>
            <TableCell>{c.handlerNm || '-'}</TableCell>
            <TableCell>
              {c.useYn === 'Y' ? (
                <Chip size="small" label="유효" color="success" variant="outlined" />
              ) : (
                <Tooltip title={c.cancelReason || ''}>
                  <Chip size="small" label="취소됨" variant="outlined" />
                </Tooltip>
              )}
            </TableCell>
            {mode === 'manage' && (
              <TableCell align="right">
                {c.useYn === 'Y' && onLinkPlan && (
                  <Select
                    size="small"
                    variant="standard"
                    displayEmpty
                    value=""
                    sx={{ minWidth: 120, mr: 1 }}
                    onChange={(e) => onLinkPlan(c, row, e.target.value)}
                  >
                    <MenuItem value="" disabled>계획에 연결</MenuItem>
                    {unfinishedPlans.map((p) => (
                      <MenuItem key={p.planUuid} value={p.planUuid}>
                        {p.planNm || '계획'} · {p.planDt}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {c.useYn === 'Y' && (
                  <Button
                    size="small"
                    color="warning"
                    disabled={!review.canCancelRow}
                    onClick={() => onCancelCollectRecord?.(c, row)}
                  >
                    이 건만 취소
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * @param review OrderBillReviewDto (서버 응답 data)
 * @param mode 'manage'(수주품의서 관리 하위패널 - 재배치/삭제 노출) | 'guard'(재작성 가드 - 체크리스트 강조)
 * @param rowBusyUuid 지금 액션 처리중인 invUuid(버튼 비활성화용)
 * @param bulkBusy 일괄 취소 처리중 여부
 */
export default function OrderBillReviewPanel({
  review,
  loading,
  mode = 'manage',
  rowBusyUuid,
  bulkBusy,
  onCancelCollection,
  onCancelCollectRecord,
  onCancelIssueComplete,
  onCancelIssueRequest,
  onRecallApproval,
  onBulkCancel,
  onDelete,
  onToggleReplan,
  replanOpen,
  onAddCollect,
  onManagePlans,
  onLinkPlan,
}) {
  // 1단계: 회차 → 계획. 2단계: 계획 → 그 계획에 배분된 수금내역(2026-10-21 수금/지급 계획 N개).
  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedPlans, setExpandedPlans] = useState(() => new Set());
  const toggleExpand = (invUuid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(invUuid)) next.delete(invUuid);
      else next.add(invUuid);
      return next;
    });
  };
  const togglePlanExpand = (planUuid) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(planUuid)) next.delete(planUuid);
      else next.add(planUuid);
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!review) return null;

  const blocking = review.blockingRows || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {review.roNo || review.roUuid} · {review.cstNm || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          담당 {review.salesUsrNm || '-'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {review.canRewrite ? (
          <Chip size="small" color="success" label="재작성 가능" />
        ) : (
          <Chip size="small" color="warning" label="재작성 불가 - 취소 필요" />
        )}
      </Box>

      <Totals review={review} />

      {mode === 'guard' && blocking.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'warning.main' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            재작성 전 취소 필요 ({blocking.length}건) - 수금(지급) 취소 → 발행 취소 순서
          </Typography>
          {blocking.map((row) => (
            <Box
              key={row.invUuid}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}
            >
              <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 600 }}>
                {row.invNo || row.invUuid}
              </Typography>
              <StatusChip label={row.statusLabel} />
              <Typography variant="body2" color="text.secondary">{fmt(row.invTot)}원</Typography>
              <Chip size="small" variant="outlined" label={row.lockReason} />
              <Box sx={{ flexGrow: 1 }} />
              {row.hasCollection && (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  disabled={!review.canCancelRow || rowBusyUuid === row.invUuid}
                  onClick={() => onCancelCollection?.(row)}
                >
                  수금(지급) 취소
                </Button>
              )}
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={!review.canCancelRow || row.hasCollection || rowBusyUuid === row.invUuid}
                onClick={() => onCancelIssueComplete?.(row)}
              >
                발행 취소
              </Button>
            </Box>
          ))}
          {!review.canCancelRow && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {review.cancelRowReason || '담당자 또는 권한자만 취소할 수 있습니다.'}
            </Typography>
          )}
        </Paper>
      )}

      {(review.groups || []).map((group) => (
        <Box key={group.billUuid} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {group.billTyp} · {group.partnerNm} (계획 {fmt(group.billAmt)})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell sx={{ fontWeight: 700 }}>계산서번호</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>상태</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>발행예정일</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>발행일</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>합계</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>수금(지급)액</TableCell>
                  {mode === 'manage' && (
                    <TableCell align="right" sx={{ fontWeight: 700 }}>액션</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(group.rows || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={mode === 'manage' ? 8 : 7} align="center" sx={{ py: 2, color: 'text.disabled' }}>
                      회차가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : group.rows.map((row) => {
                  const busy = rowBusyUuid === row.invUuid;
                  const collects = row.collects || [];
                  const plans = row.plans || [];
                  const unlinkedCollects = collects.filter((c) => !c.planUuid);
                  const isOpen = expanded.has(row.invUuid);
                  return (
                    <React.Fragment key={row.invUuid}>
                      <TableRow sx={row.lockReason ? { backgroundColor: 'action.hover' } : undefined}>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleExpand(row.invUuid)}>
                            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{row.invNo || '-'}</TableCell>
                        <TableCell><StatusChip label={row.statusLabel} /></TableCell>
                        <TableCell>
                          {row.reqPlanDt || '-'}
                          {row.overdueIssue && (
                            <Chip size="small" color="error" label="발행 지연" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.reqCompDt || '-'}
                          {row.overdueCollect && (
                            <Chip size="small" color="error" label="수금(지급) 지연" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(row.invTot)}</TableCell>
                        <TableCell align="right">{fmt(row.collectedAmt)}</TableCell>
                        {mode === 'manage' && (
                          <TableCell align="right">
                            <Tooltip title={review.canCancelRow ? '' : (review.cancelRowReason || '')}>
                              <span>
                                {row.reqCompYn === 'Y' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!review.canCancelRow || busy}
                                    onClick={() => onAddCollect?.(row)}
                                  >
                                    수금등록
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  disabled={busy}
                                  onClick={() => onManagePlans?.(row)}
                                >
                                  계획관리
                                </Button>
                                {row.hasCollection && (
                                  <Button
                                    size="small"
                                    disabled={!review.canCancelRow || busy}
                                    onClick={() => onCancelCollection?.(row)}
                                  >
                                    수금취소(전체)
                                  </Button>
                                )}
                                {row.reqCompYn === 'Y' && !row.hasCollection && (
                                  <Button
                                    size="small"
                                    color="error"
                                    disabled={!review.canCancelRow || busy}
                                    onClick={() => onCancelIssueComplete?.(row)}
                                  >
                                    발행취소
                                  </Button>
                                )}
                                {row.statusLabel === '결재중' && (
                                  <Button
                                    size="small"
                                    color="warning"
                                    disabled={!review.canCancelRow || busy}
                                    onClick={() => onRecallApproval?.(row)}
                                  >
                                    결재 회수
                                  </Button>
                                )}
                                {row.statusLabel !== '결재중' && row.reqCompYn !== 'Y' && row.reqPlanYn === 'Y' && (
                                  <Button
                                    size="small"
                                    disabled={!review.canCancelRow || busy}
                                    onClick={() => onCancelIssueRequest?.(row)}
                                  >
                                    발행요청취소
                                  </Button>
                                )}
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={mode === 'manage' ? 8 : 7} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ py: 1, pl: 5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                계획 {plans.length}개 - 세금계산서 1건에 수금(지급) 계획이 N개(선금/중도금/잔금) 있을 수 있다
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 36 }} />
                                    <TableCell>계획명</TableCell>
                                    <TableCell>메모</TableCell>
                                    <TableCell>계획일</TableCell>
                                    <TableCell align="right">계획금액</TableCell>
                                    <TableCell align="right">배분액</TableCell>
                                    <TableCell align="right">잔액</TableCell>
                                    <TableCell>상태</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {plans.map((p) => {
                                    const planCollects = collects.filter((c) => c.planUuid === p.planUuid);
                                    const planOpen = expandedPlans.has(p.planUuid);
                                    return (
                                      <React.Fragment key={p.planUuid}>
                                        <TableRow>
                                          <TableCell>
                                            {planCollects.length > 0 && (
                                              <IconButton size="small" onClick={() => togglePlanExpand(p.planUuid)}>
                                                {planOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                              </IconButton>
                                            )}
                                          </TableCell>
                                          <TableCell>{p.planNm || '-'}</TableCell>
                                          <TableCell>{p.planMemo || '-'}</TableCell>
                                          <TableCell>{p.planDt || '-'}</TableCell>
                                          <TableCell align="right">{fmt(p.planAmt)}</TableCell>
                                          <TableCell align="right">{fmt(p.allocatedAmt)}</TableCell>
                                          <TableCell align="right">{fmt(p.remainAmt)}</TableCell>
                                          <TableCell><PlanStatusChip label={p.status} locked={p.locked} /></TableCell>
                                        </TableRow>
                                        {planCollects.length > 0 && (
                                          <TableRow>
                                            <TableCell colSpan={8} sx={{ py: 0 }}>
                                              <Collapse in={planOpen} unmountOnExit>
                                                <Box sx={{ pl: 5, py: 0.5 }}>
                                                  <CollectRows
                                                    collects={planCollects}
                                                    mode={mode}
                                                    review={review}
                                                    row={row}
                                                    onCancelCollectRecord={onCancelCollectRecord}
                                                  />
                                                </Box>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {unlinkedCollects.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    계획 없이 등록된 수금(지급) {unlinkedCollects.length}건 - 계획을 선택해 연결할 수 있습니다
                                  </Typography>
                                  <CollectRows
                                    collects={unlinkedCollects}
                                    mode={mode}
                                    review={review}
                                    row={row}
                                    onCancelCollectRecord={onCancelCollectRecord}
                                    onLinkPlan={onLinkPlan}
                                    plans={plans}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {mode === 'manage' && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" variant="outlined" onClick={onToggleReplan}>
              {replanOpen ? '재배치 닫기' : '재배치'}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={review.canBulkCancel ? '' : '권한자만 일괄 취소할 수 있습니다.'}>
              <span>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  disabled={!review.canBulkCancel || bulkBusy}
                  onClick={onBulkCancel}
                >
                  권한자 일괄 취소
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={review.canRewrite ? '' : '발행완료된 세금계산서가 남아있어 삭제할 수 없습니다.'}>
              <span>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={!review.canRewrite || !review.canCancelRow}
                  onClick={onDelete}
                >
                  삭제
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
}
